// src/services/contract.service.ts
import { db, storage } from '../config/firebase';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import {
  Contract,
  ContractStatus,
  SignatureRecord,
  UserSignatureProfile,
  ContractRequest,
  CreateContractDto,
  ContractTemplate,
  ContractTemplateType,
  SigningAuditEvent
} from '../types/contract.types';
import { Task } from '../types/task.types';
import { toast } from 'react-hot-toast';

// Helper function to recursively remove undefined values from objects
// Firebase Firestore does not accept undefined values
function removeUndefined(obj: any): any {
  if (obj === null || obj === undefined) {
    return null;
  }
  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefined(item));
  }
  if (typeof obj === 'object' && !(obj instanceof Timestamp)) {
    const cleaned: Record<string, any> = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = removeUndefined(value);
      }
    }
    return cleaned;
  }
  return obj;
}

export const contractService = {
  async createContract(
    data: CreateContractDto,
    organizationId: string,
    createdBy: string,
    createdByName: string
  ): Promise<string> {
    try {
      const contractRef = doc(collection(db, 'contracts'));

      // Upload document to Firebase Storage
      const timestamp = Date.now();
      const fileName = `${timestamp}_${data.documentFile.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
      const storageRef = ref(storage, `contracts/${contractRef.id}/${fileName}`);
      await uploadBytes(storageRef, data.documentFile);
      const documentUrl = await getDownloadURL(storageRef);

      // Build contract object, excluding undefined values (Firebase doesn't accept undefined)
      const contract: Record<string, any> = {
        id: contractRef.id,
        organizationId,
        title: data.title,
        documentUrl,
        documentName: data.documentFile.name,
        status: 'draft',
        signatureFields: [],
        signatures: [],
        requiredSigners: data.requiredSigners.map(s => ({
          ...s,
          signed: false,
          mandatory: s.mandatory || false
        })),
        createdBy,
        createdByName,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        mandatory: data.requiredSigners.some(s => s.mandatory), // True if any signer is mandatory
        contractType: data.contractType || 'general'
      };

      // Only add optional fields if they have values
      if (data.projectId) contract.projectId = data.projectId;
      if (data.description) contract.description = data.description;
      if (data.expiresAt) contract.expiresAt = Timestamp.fromDate(data.expiresAt);
      if (data.templateId) contract.templateId = data.templateId;

      await setDoc(contractRef, contract);
      return contractRef.id;
    } catch (error) {
      console.error('Error creating contract:', error);
      throw error;
    }
  },

  async getContract(contractId: string): Promise<Contract | null> {
    const docRef = doc(db, 'contracts', contractId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data() as Contract;
    }
    return null;
  },

  async getOrganizationContracts(organizationId: string): Promise<Contract[]> {
    const q = query(
      collection(db, 'contracts'),
      where('organizationId', '==', organizationId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data() as Contract);
  },

  async getAllContracts(): Promise<Contract[]> {
    const q = query(
      collection(db, 'contracts'),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data() as Contract);
  },

  async getAllTemplates(): Promise<ContractTemplate[]> {
    const q = query(
      collection(db, 'contractTemplates'),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data() as ContractTemplate);
  },

  async getUserContracts(userId: string): Promise<Contract[]> {
    const q = query(
      collection(db, 'contracts'),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    const contracts = snapshot.docs.map(doc => doc.data() as Contract);

    // Filter to contracts where user is a required signer
    return contracts.filter(c =>
      c.requiredSigners.some(s => s.userId === userId)
    );
  },

  async updateContractStatus(contractId: string, status: ContractStatus): Promise<void> {
    const contractRef = doc(db, 'contracts', contractId);
    await updateDoc(contractRef, {
      status,
      updatedAt: Timestamp.now(),
      ...(status === 'completed' ? { completedAt: Timestamp.now() } : {})
    });
  },

  // Add signature to contract with full legal defensibility data
  async addSignature(
    contractId: string,
    signature: Omit<SignatureRecord, 'signedAt'>,
    certificateBlob?: Blob
  ): Promise<void> {
    const contract = await this.getContract(contractId);
    if (!contract) throw new Error('Contract not found');

    let certificateUrl: string | undefined;

    // Upload signing certificate if provided
    if (certificateBlob) {
      const timestamp = Date.now();
      const fileName = `signing_certificate_${signature.signedBy}_${timestamp}.pdf`;
      const certificateRef = ref(storage, `contracts/${contractId}/certificates/${fileName}`);
      await uploadBytes(certificateRef, certificateBlob);
      certificateUrl = await getDownloadURL(certificateRef);
    }

    // Build signature record, excluding undefined values (Firebase doesn't accept undefined)
    const signatureRecord: Record<string, any> = {
      fieldId: signature.fieldId || 'main',
      signedBy: signature.signedBy,
      signedByName: signature.signedByName || 'Unknown',
      signedByEmail: signature.signedByEmail || 'Unknown',
      signedAt: Timestamp.now(),
      signatureData: signature.signatureData,
      signatureType: signature.signatureType || 'drawn',
      ipAddress: signature.ipAddress || 'unknown',
      userAgent: signature.userAgent || 'unknown',
      documentHash: signature.documentHash || 'unavailable',
      auditTrail: signature.auditTrail || []
    };

    // Only add optional fields if they have values
    if (signature.geolocation) {
      signatureRecord.geolocation = signature.geolocation;
    }
    if (certificateUrl) {
      signatureRecord.certificateUrl = certificateUrl;
    }

    // Clean existing signatures to remove any undefined values
    const cleanedExistingSignatures = removeUndefined(contract.signatures || []);

    // Update signatures array
    const updatedSignatures = [...cleanedExistingSignatures, signatureRecord];

    // Update signer status - clean each signer to remove undefined values
    const updatedSigners = (contract.requiredSigners || []).map(s => {
      const cleanedSigner = removeUndefined(s);
      if (cleanedSigner.userId === signature.signedBy) {
        return { ...cleanedSigner, signed: true };
      }
      return cleanedSigner;
    });

    // Check if all signers have signed
    const allSigned = updatedSigners.every((s: any) => s.signed);
    const newStatus: ContractStatus = allSigned
      ? 'completed'
      : updatedSigners.some((s: any) => s.signed)
        ? 'partially_signed'
        : contract.status;

    // Build update object and clean it to ensure no undefined values
    const updateData: Record<string, any> = {
      signatures: updatedSignatures,
      requiredSigners: updatedSigners,
      status: newStatus,
      updatedAt: Timestamp.now()
    };

    if (newStatus === 'completed') {
      updateData.completedAt = Timestamp.now();
    }

    await updateDoc(doc(db, 'contracts', contractId), removeUndefined(updateData));
  },

  // Set document hash on contract (called when document is first opened for signing)
  async setDocumentHash(contractId: string, documentHash: string): Promise<void> {
    const contractRef = doc(db, 'contracts', contractId);
    await updateDoc(contractRef, {
      documentHash,
      updatedAt: Timestamp.now()
    });
  },

  // Request signature from user
  async requestSignature(
    contractId: string,
    requestedBy: string,
    requestedByName: string,
    requestedTo: string,
    requestedToName: string,
    requestedToEmail: string,
    message?: string
  ): Promise<string> {
    const requestRef = doc(collection(db, 'contractRequests'));

    // Build request object without undefined values (Firebase doesn't accept undefined)
    const request: Record<string, any> = {
      id: requestRef.id,
      contractId,
      requestedBy,
      requestedByName,
      requestedTo,
      requestedToName,
      requestedToEmail,
      status: 'pending',
      sentAt: Timestamp.now()
    };

    // Only add message if provided
    if (message) {
      request.message = message;
    }

    await setDoc(requestRef, request);

    // Update contract status to pending_signatures if in draft
    const contract = await this.getContract(contractId);
    if (contract && contract.status === 'draft') {
      await this.updateContractStatus(contractId, 'pending_signatures');
    }

    return requestRef.id;
  },

  async saveSignatureProfile(
    userId: string,
    signatureData: string,
    signatureType: 'drawn' | 'typed' | 'uploaded',
    initials?: string
  ): Promise<void> {
    const profileRef = doc(db, 'userSignatureProfiles', userId);

    // Build profile object without undefined values (Firebase doesn't accept undefined)
    const profile: Record<string, any> = {
      userId,
      signatureData,
      signatureType,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    // Only add initials if provided
    if (initials) {
      profile.initials = initials;
    }

    await setDoc(profileRef, profile, { merge: true });
  },

  async getSignatureProfile(userId: string): Promise<UserSignatureProfile | null> {
    const docRef = doc(db, 'userSignatureProfiles', userId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data() as UserSignatureProfile;
    }
    return null;
  },

  async deleteContract(contractId: string): Promise<void> {
    const contract = await this.getContract(contractId);
    if (contract) {
      // Delete document from storage
      try {
        const storageRef = ref(storage, `contracts/${contractId}`);
        await deleteObject(storageRef);
      } catch (e) {
        // File may not exist
      }
      await deleteDoc(doc(db, 'contracts', contractId));
    }
  },

  // Create contract signature task for a user
  async createContractSignatureTask(data: {
    organizationId: string;
    projectId?: string;
    projectName?: string;
    contractId: string;
    contractTitle: string;
    assignedTo: string;
    assignedToName: string;
    assignedBy: string;
    assignedByName: string;
  }): Promise<string> {
    // Due in 3 days (urgent priority for contracts)
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 3);
    dueDate.setHours(17, 0, 0, 0);

    const taskRef = doc(collection(db, 'tasks'));
    const task: Task = {
      id: taskRef.id,
      organizationId: data.organizationId,
      projectId: data.projectId,
      projectName: data.projectName,
      type: 'contract_signature',
      title: `Sign Contract: ${data.contractTitle}`,
      description: `You have a new contract to sign: "${data.contractTitle}".\n\nPlease review and sign this contract at your earliest convenience.`,
      status: 'pending',
      priority: 'urgent',
      assignedTo: data.assignedTo,
      assignedToName: data.assignedToName,
      assignedBy: data.assignedBy,
      assignedByName: data.assignedByName,
      contractId: data.contractId,
      dueDate: Timestamp.fromDate(dueDate),
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      autoGenerated: true,
      source: 'contract_signature'
    };

    await setDoc(taskRef, task);
    return taskRef.id;
  },

  // Complete the task when contract is signed
  async completeContractTask(contractId: string, userId: string): Promise<void> {
    const q = query(
      collection(db, 'tasks'),
      where('contractId', '==', contractId),
      where('assignedTo', '==', userId),
      where('type', '==', 'contract_signature')
    );

    const snapshot = await getDocs(q);
    for (const taskDoc of snapshot.docs) {
      await updateDoc(doc(db, 'tasks', taskDoc.id), {
        status: 'completed',
        completedAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
    }
  },

  // Get mandatory unsigned contracts for a user
  async getMandatoryUnsignedContracts(userId: string): Promise<Contract[]> {
    const q = query(
      collection(db, 'contracts'),
      where('mandatory', '==', true),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    const contracts = snapshot.docs.map(doc => doc.data() as Contract);

    // Filter to contracts where user is a required signer with mandatory=true and hasn't signed
    return contracts.filter(c =>
      c.requiredSigners.some(s => s.userId === userId && !s.signed && s.mandatory === true)
    );
  },

  // Check if user has any mandatory unsigned contracts
  async hasMandatoryUnsignedContracts(userId: string): Promise<boolean> {
    const contracts = await this.getMandatoryUnsignedContracts(userId);
    return contracts.length > 0;
  },

  // Contract Templates
  async createTemplate(
    data: {
      name: string;
      description?: string;
      documentFile: File;
      templateType: ContractTemplateType;
      mandatory: boolean;
    },
    organizationId: string,
    createdBy: string,
    createdByName: string
  ): Promise<string> {
    try {
      const templateRef = doc(collection(db, 'contractTemplates'));

      // Upload document to Firebase Storage
      const timestamp = Date.now();
      const fileName = `${timestamp}_${data.documentFile.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
      const storageRef = ref(storage, `contractTemplates/${templateRef.id}/${fileName}`);
      await uploadBytes(storageRef, data.documentFile);
      const documentUrl = await getDownloadURL(storageRef);

      const template: ContractTemplate = {
        id: templateRef.id,
        organizationId,
        name: data.name,
        description: data.description,
        documentUrl,
        documentName: data.documentFile.name,
        templateType: data.templateType,
        mandatory: data.mandatory,
        createdBy,
        createdByName,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      await setDoc(templateRef, template);
      return templateRef.id;
    } catch (error) {
      console.error('Error creating template:', error);
      throw error;
    }
  },

  async getTemplates(organizationId: string): Promise<ContractTemplate[]> {
    const q = query(
      collection(db, 'contractTemplates'),
      where('organizationId', '==', organizationId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data() as ContractTemplate);
  },

  async getTemplate(templateId: string): Promise<ContractTemplate | null> {
    const docRef = doc(db, 'contractTemplates', templateId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data() as ContractTemplate;
    }
    return null;
  },

  async deleteTemplate(templateId: string): Promise<void> {
    const template = await this.getTemplate(templateId);
    if (template) {
      try {
        const storageRef = ref(storage, `contractTemplates/${templateId}`);
        await deleteObject(storageRef);
      } catch (e) {
        // File may not exist
      }
      await deleteDoc(doc(db, 'contractTemplates', templateId));
    }
  },

  // Create contract from template
  async createContractFromTemplate(
    templateId: string,
    signers: {
      userId: string;
      userName: string;
      email: string;
      role: string;
      payRate?: number;
      payType?: 'hourly' | 'per_lead' | 'flat';
      mandatory?: boolean;
    }[],
    organizationId: string,
    createdBy: string,
    createdByName: string,
    projectId?: string
  ): Promise<string> {
    const template = await this.getTemplate(templateId);
    if (!template) throw new Error('Template not found');

    const contractRef = doc(collection(db, 'contracts'));

    // Build signers without undefined values
    const cleanedSigners = signers.map(s => {
      const signer: Record<string, any> = {
        userId: s.userId,
        userName: s.userName,
        email: s.email,
        role: s.role,
        signed: false,
        mandatory: s.mandatory || false
      };
      // Only add optional fields if they have values
      if (s.payRate !== undefined) signer.payRate = s.payRate;
      if (s.payType !== undefined) signer.payType = s.payType;
      return signer;
    });

    // Build contract object without undefined values
    const contract: Record<string, any> = {
      id: contractRef.id,
      organizationId,
      title: template.name,
      documentUrl: template.documentUrl,
      documentName: template.documentName,
      status: signers.length > 0 ? 'pending_signatures' : 'draft',
      signatureFields: [],
      signatures: [],
      requiredSigners: cleanedSigners,
      createdBy,
      createdByName,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      mandatory: signers.some(s => s.mandatory),
      templateId: template.id,
      contractType: template.templateType === 'w9' ? 'w9' :
                    template.templateType === 'direct_deposit' ? 'direct_deposit' : 'general'
    };

    // Only add optional fields if they have values
    if (projectId) contract.projectId = projectId;
    if (template.description) contract.description = template.description;

    await setDoc(contractRef, contract);
    return contractRef.id;
  },

  /**
   * Assign all mandatory contract templates to a new user
   * Called during user onboarding
   */
  async assignMandatoryContractsToUser(
    userId: string,
    userName: string,
    organizationId: string,
    createdBy: string,
    createdByName: string
  ): Promise<string[]> {
    const assignedContractIds: string[] = [];

    try {
      // Get all mandatory templates for this organization
      const templates = await this.getTemplates(organizationId);
      const mandatoryTemplates = templates.filter(t => t.mandatory);

      for (const template of mandatoryTemplates) {
        try {
          const contractId = await this.createContractFromTemplate(
            template.id,
            organizationId,
            createdBy,
            createdByName,
            [{ userId, name: userName, mandatory: true }]
          );
          assignedContractIds.push(contractId);
        } catch (error) {
          console.error(`Failed to assign contract template ${template.id} to user ${userId}:`, error);
          // Continue with other templates even if one fails
        }
      }

      return assignedContractIds;
    } catch (error) {
      console.error('Error assigning mandatory contracts:', error);
      return assignedContractIds;
    }
  }
};
